import { h } from "@/lib/dom";
import { button } from "@/components/ui";
import { icon } from "@/lib/icons";
import { navigate, type RouteContext } from "@/app/router";
import {
  MARK_MEANING,
  segmentAnswer,
  type ExamGrade,
  type GradeMark,
  type GradeRow,
} from "@/features/klausur/correction";

export type { ExamGrade } from "@/features/klausur/correction";

/**
 * Auswertung der Probeklausur – aufgebaut wie eine zurückgegebene Klausur:
 *
 *   1. Note und Punkte
 *   2. die eigene Bearbeitung MIT Randnotizen (unterstrichene Stellen, am Rand
 *      Kürzel und Anmerkung)
 *   3. Bewertungsmatrix nach Bereichen
 *   4. Schlusswort und Übe-Tipps
 *
 * Die Randnotizen sind der Kern: erst an ihnen sieht man, welche Stelle gemeint
 * ist. Eine Prozentzahl allein sagt nicht, was zu ändern wäre.
 */

const MARK_CLASS: Record<GradeMark, string> = { "++": "pp", "+": "p", o: "o", "-": "m", "--": "mm" };
const MARK_LABEL: Record<GradeMark, string> = {
  "++": "Sehr gut",
  "+": "Gut",
  o: "Befriedigend",
  "-": "Schwach",
  "--": "Ungenügend",
};

export function examResultKey(topicId: string): string {
  return `exam_result_${topicId}`;
}

export function ExamResultPage(ctx: RouteContext): HTMLElement {
  const topicId = ctx.params.id;
  const raw = sessionStorage.getItem(examResultKey(topicId));

  if (!raw) {
    return h("div", { class: "card card-pad" },
      h("p", { class: "muted" }, "Keine Auswertung gefunden. Bitte erst eine Probeklausur bearbeiten."),
      button({ label: "Zur Übungsübersicht", variant: "ghost", onClick: () => navigate(`/topic/${topicId}/learn`) }));
  }

  let g: ExamGrade;
  try {
    g = JSON.parse(raw) as ExamGrade;
  } catch {
    return h("div", { class: "card card-pad field-error" }, "Auswertung konnte nicht geladen werden.");
  }

  return renderGradePage(g, topicId);
}

export function renderGradePage(g: ExamGrade, topicId: string): HTMLElement {
  const pct = Math.max(0, Math.min(100, Number(g.percent) || 0));
  const hue = Math.round((pct / 100) * 120);
  const rows = Array.isArray(g.rows) ? g.rows : [];

  return h(
    "div",
    { class: "exam-result-page" },
    h(
      "div",
      { class: "topic-top" },
      button({ icon: "chevron-left", variant: "ghost", title: "Zurück", onClick: () => navigate(`/topic/${topicId}/learn`) }),
      h("div", { class: "t-headings" },
        h("h1", null, "Korrektur"),
        g.topicTitle ? h("div", { class: "topic-crumb" }, g.topicTitle) : null)
    ),

    // 1 – Note
    h(
      "div",
      { class: "card grade-hero" },
      h("div", { class: "grade-badge", style: `background:hsl(${hue} 65% 45%)` }, g.grade || `${pct}%`),
      h(
        "div",
        { class: "grade-hero-text" },
        h("h2", null, "Dein Ergebnis"),
        h("div", { class: "muted" },
          g.maxPoints ? `${g.points} von ${g.maxPoints} Punkten · ${pct}%` : `${pct}% erreicht`),
        h("div", { class: "grade-bar-wrap" },
          h("div", { class: "grade-bar-fill", style: `width:${pct}%;background:hsl(${hue} 65% 45%)` }))
      )
    ),

    // 2 – Bearbeitung mit Randnotizen
    g.answer ? markedSheet(g) : null,

    // 3 – Bewertungsmatrix nach Bereichen
    rows.length ? matrixCard(rows) : null,

    // 4 – Schlusswort
    h(
      "div",
      { class: "card card-pad grade-feedback" },
      h("h3", { class: "section-title", style: "margin-bottom:8px" }, "Lehrerfeedback"),
      h("div", { class: "lt-feedback teacher ok", style: "border:none;background:transparent;padding:0" }, g.summary || ""),
      g.strengths
        ? h("div", { class: "grade-strengths" }, h("span", { html: icon("check", 16) }), h("span", null, g.strengths))
        : null
    ),

    g.improve?.length
      ? h("div", { class: "card card-pad" },
          h("h3", { class: "section-title", style: "margin-bottom:10px" }, "Das solltest du noch üben"),
          h("ul", { class: "grade-todo" }, ...g.improve.map((tip) => h("li", null, tip))))
      : null,

    h(
      "div",
      { class: "row", style: "gap:10px;flex-wrap:wrap" },
      button({ label: "Neue Probeklausur", icon: "pencil", variant: "primary",
        onClick: () => navigate(`/topic/${topicId}/learn/klausur`) }),
      button({ label: "Zur Übungsübersicht", variant: "ghost", onClick: () => navigate(`/topic/${topicId}/learn`) }),
      button({ label: "Zum Thema", variant: "ghost", onClick: () => navigate(`/topic/${topicId}`) })
    )
  );
}

/* ------------------------- Bearbeitung mit Randnotizen ------------------------- */

function markedSheet(g: ExamGrade): HTMLElement {
  const notes = Array.isArray(g.notes) ? g.notes : [];
  const segments = segmentAnswer(g.answer ?? "", notes);

  // Der Text steht links, die Notizen stehen rechts am Rand – wie auf Papier.
  // Beide tragen dieselbe Nummer, damit die Zuordnung auch dann klar bleibt,
  // wenn auf dem Handy alles untereinander rutscht.
  const textEl = h("div", { class: "corr-text" });
  for (const seg of segments) {
    if (!seg.note) {
      textEl.appendChild(document.createTextNode(seg.text));
      continue;
    }
    const cls = seg.note.mark === "+" || seg.note.mark === "++" ? "praise" : "fix";
    textEl.appendChild(
      h(
        "mark",
        { class: `corr-mark ${cls}`, title: `${seg.note.mark} – ${seg.note.note}` },
        seg.text,
        h("sup", { class: "corr-num" }, String(seg.index))
      )
    );
  }

  const marginEl = h(
    "div",
    { class: "corr-margin abs" },
    ...segments
      .filter((s) => s.note)
      .map((s) => {
        const note = s.note!;
        const praise = note.mark === "+" || note.mark === "++";
        return h(
          "div",
          { class: "corr-note" + (praise ? " praise" : "") },
          h("span", { class: "corr-note-num" }, String(s.index)),
          h(
            "div",
            null,
            h("span", { class: "corr-sign", title: MARK_MEANING[note.mark] ?? "" }, note.mark),
            h("span", { class: "corr-note-txt" }, note.note),
            note.better ? h("div", { class: "corr-better" }, "besser: ", h("i", null, note.better)) : null
          )
        );
      })
  );

  const legend = h(
    "div",
    { class: "corr-legend" },
    ...[...new Set(notes.map((n) => n.mark))].map((m) =>
      h("span", null, h("b", null, m), MARK_MEANING[m] ? ` = ${MARK_MEANING[m]}` : "")
    )
  );

  // Jede Notiz wandert auf die Höhe ihrer markierten Stelle; überlappende
  // werden nach unten geschoben.
  const align = () => {
    const marks = [...textEl.querySelectorAll<HTMLElement>(".corr-mark")];
    const notes = [...marginEl.querySelectorAll<HTMLElement>(".corr-note")];
    let bottom = 0;
    marks.forEach((m, i) => {
      const note = notes[i];
      if (!note) return;
      const top = Math.max(bottom, m.offsetTop);
      note.style.top = `${top}px`;
      bottom = top + note.offsetHeight + 8;
    });
    marginEl.style.height = `${bottom}px`;
  };
  requestAnimationFrame(align);
  window.addEventListener("resize", align);

  return h(
    "div",
    { class: "card card-pad" },
    h("h3", { class: "section-title", style: "margin-bottom:4px" }, "Deine Bearbeitung mit Anmerkungen"),
    h("p", { class: "muted text-sm", style: "margin:0 0 12px" },
      notes.length
        ? `${notes.length} Anmerkung${notes.length === 1 ? "" : "en"} am Rand – markierte Stellen antippen für den Hinweis.`
        : "Keine einzelnen Anmerkungen – der Text war sprachlich sauber."),
    h("div", { class: "corr-sheet" }, textEl, marginEl),
    notes.length ? legend : null
  );
}

/* ------------------------------ Bewertungsmatrix ------------------------------ */

function matrixCard(rows: GradeRow[]): HTMLElement {
  // Nach Bereichen gruppieren: „Inhalt", „Methode", „Sprache", „Form". Elf
  // Kriterien in einer flachen Liste liest niemand mehr.
  const order = ["Inhalt", "Methode", "Sprache", "Form"];
  const groups = new Map<string, GradeRow[]>();
  for (const r of rows) {
    const area = r.area || "Inhalt";
    const list = groups.get(area) ?? [];
    list.push(r);
    groups.set(area, list);
  }
  const sorted = [...groups.entries()].sort(
    (a, b) => (order.indexOf(a[0]) + 99) % 100 - ((order.indexOf(b[0]) + 99) % 100)
  );

  return h(
    "div",
    { class: "card card-pad" },
    h("h3", { class: "section-title", style: "margin-bottom:12px" }, "Bewertungsmatrix"),
    ...sorted.flatMap(([area, list]) => {
      const got = list.reduce((n, r) => n + (r.points || 0), 0);
      const max = list.reduce((n, r) => n + (r.maxPoints || 0), 0);
      return [
        h("div", { class: "gm-area" },
          h("span", null, area),
          max ? h("span", { class: "gm-area-pts" }, `${got} / ${max} P.`) : null),
        h("div", { class: "grade-matrix-grid" },
          ...list.map((r) => {
            const cls = MARK_CLASS[r.mark] ?? "o";
            return h("div", { class: `grade-matrix-row gm-${cls}` },
              h("div", { class: "gm-criterion" }, r.criterion),
              h("div", { class: "gm-mark-badge" },
                h("span", { class: `gm-mark ${cls}` }, r.mark),
                h("span", { class: "gm-mark-label" }, MARK_LABEL[r.mark] ?? "")),
              r.maxPoints ? h("div", { class: "gm-points" }, `${r.points} / ${r.maxPoints}`) : null,
              r.comment ? h("div", { class: "gm-comment muted text-sm" }, r.comment) : null);
          })),
      ];
    })
  );
}
